import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Alert,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { useAppSelector } from '../store/Hooks';
import UserService from '../services/UserService';
import SellerService from '../services/SellerService';
import Utils from '../utils/Utils';
import translator from '../theme/translator.json';
import SupportedLanguages from '../models/enums/SupportedLanguages';
import ClientType from '../models/ClientType';
import User from '../models/User';

interface TransferClientsDialogProps {
  open: boolean;
  onClose: () => void;
  selectedClients: ClientType[];
  onTransferComplete: () => void;
}

interface TransferResult {
  clientId: string;
  clientName: string;
  success: boolean;
  message: string;
}

const TransferClientsDialog: React.FC<TransferClientsDialogProps> = ({
  open,
  onClose,
  selectedClients,
  onTransferComplete,
}) => {
  const { user } = useAppSelector((state) => state.user);
  const { originalUser } = useAppSelector((state) => state.shadowUser);
  const language = useAppSelector((state) => state.user.language) as SupportedLanguages;

  const [sellers, setSellers] = useState<User[]>([]);
  const [selectedSellerId, setSelectedSellerId] = useState<string>('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [transferResults, setTransferResults] = useState<TransferResult[]>([]);
  const [currentTransferIndex, setCurrentTransferIndex] = useState(0);

  // Obtenir l'utilisateur principal (celui qui peut faire des transferts)
  const mainUser = originalUser || user;

  useEffect(() => {
    const loadSellers = async () => {
      if (!mainUser?.id) return;
      try {
        const sellersList = await UserService.getAllSellers(mainUser.id);
        const options = [...sellersList, { ...mainUser, name: 'Moi-même' }];
        setSellers(options);
      } catch (error) {
        console.error('Error loading sellers:', error);
      }
    };

    if (open && mainUser) {
      loadSellers();
    }
  }, [open, mainUser]);

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes
      setSelectedSellerId('');
      setShowConfirmation(false);
      setTransferResults([]);
      setCurrentTransferIndex(0);
      setIsTransferring(false);
    }
  }, [open]);

  const handleSellerChange = (event: SelectChangeEvent<string>) => {
    setSelectedSellerId(event.target.value);
  };

  const handleNextStep = () => {
    if (selectedSellerId && selectedClients.length > 0) {
      setShowConfirmation(true);
    }
  };

  const handleConfirmTransfer = async () => {
    if (!selectedSellerId || !mainUser) return;

    setIsTransferring(true);
    setTransferResults([]);
    setCurrentTransferIndex(0);

    const selectedSeller = sellers.find((s) => s.id === selectedSellerId);
    const sellerName = selectedSeller?.name || 'Unknown';

    // Traitement séquentiel des transferts avec reduce pour éviter ESLint warnings
    await selectedClients.reduce(async (previousPromise, client, index) => {
      await previousPromise;
      setCurrentTransferIndex(index + 1);

      try {
        console.log(client.ownerId);
        console.log(user.id);
        console.log(selectedSellerId);
        console.log(client.id);
        await SellerService.transferClient(client.ownerId || user.id, selectedSellerId, client.id);

        const successMessage = Utils.getTranslation(
          language,
          translator.components.transferClientsDialog.transferedTo,
        ).replace('{sellerName}', sellerName);

        setTransferResults((prev) => [...prev, {
          clientId: client.id,
          clientName: client.name || 'Client sans nom',
          success: true,
          message: successMessage,
        }]);
      } catch (error: any) {
        setTransferResults((prev) => [...prev, {
          clientId: client.id,
          clientName: client.name || 'Client sans nom',
          success: false,
          message: error.message || Utils.getTranslation(language, translator.components.transferClientsDialog.error),
        }]);
      }
    }, Promise.resolve());

    setIsTransferring(false);

    // Auto-fermer après un délai si tous les transferts ont réussi
    const allSuccessful = transferResults.filter((r) => r.success).length === selectedClients.length;
    if (allSuccessful) {
      setTimeout(() => {
        onTransferComplete();
        onClose();
      }, 2000);
    }
  };

  const handleBack = () => {
    setShowConfirmation(false);
  };

  const handleClose = () => {
    if (!isTransferring) {
      onClose();
    }
  };

  const getProgressPercentage = () => {
    if (selectedClients.length === 0) return 0;
    return (currentTransferIndex / selectedClients.length) * 100;
  };

  const canTransfer = () => selectedSellerId && selectedClients.length > 0 && !isTransferring;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isTransferring}
    >
      <DialogTitle>
        {Utils.getTranslation(language, translator.components.transferClientsDialog.title)}
      </DialogTitle>

      <DialogContent>
        {!showConfirmation ? (
          // Étape 1: Sélection du vendeur
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel>
                {Utils.getTranslation(language, translator.components.transferClientsDialog.selectSeller)}
              </InputLabel>
              <Select
                value={selectedSellerId}
                onChange={handleSellerChange}
                label={Utils.getTranslation(language, translator.components.transferClientsDialog.selectSeller)}
              >
                {sellers.map((seller) => (
                  <MenuItem key={seller.id} value={seller.id}>
                    {seller.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
              {Utils.getTranslation(language, translator.components.transferClientsDialog.selectedClients)}
            </Typography>

            <List dense>
              {selectedClients.map((client) => (
                <ListItem key={client.id}>
                  <ListItemText primary={client.name} secondary={client.email} />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          // Étape 2: Confirmation et transfert
          <>
            {!isTransferring && transferResults.length === 0 && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {Utils.getTranslation(language, translator.components.transferClientsDialog.confirmTransfer)}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {Utils.getTranslation(language, translator.components.transferClientsDialog.selectedClients)}
                </Typography>

                <List dense>
                  {selectedClients.map((client) => (
                    <ListItem key={client.id}>
                      <ListItemText primary={client.name} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {isTransferring && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {Utils.getTranslation(language, translator.components.transferClientsDialog.transferring)}
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={getProgressPercentage()}
                  sx={{ mb: 2 }}
                />

                <Typography variant="body2" color="text.secondary">
                  {currentTransferIndex} / {selectedClients.length}
                </Typography>
              </Box>
            )}

            {transferResults.length > 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Résultats du transfert:
                </Typography>

                {transferResults.map((result, index) => (
                  <Alert
                    key={result.clientId}
                    severity={result.success ? 'success' : 'error'}
                    sx={{ mb: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <strong>{result.clientName}</strong>
                      <Chip
                        label={result.success
                          ? Utils.getTranslation(language, translator.components.transferClientsDialog.success)
                          : Utils.getTranslation(language, translator.components.transferClientsDialog.error)
                        }
                        color={result.success ? 'success' : 'error'}
                        size="small"
                      />
                      <Typography variant="body2">
                        {result.message}
                      </Typography>
                    </Box>
                  </Alert>
                ))}

                {!isTransferring && transferResults.length === selectedClients.length && (
                  <Typography variant="body1" sx={{ mt: 2, textAlign: 'center', color: 'success.main' }}>
                    {Utils.getTranslation(language, translator.components.transferClientsDialog.transferComplete)}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!showConfirmation ? (
          <>
            <Button onClick={handleClose}>
              {Utils.getTranslation(language, translator.general.cancel)}
            </Button>
            <Button
              onClick={handleNextStep}
              variant="contained"
              disabled={!canTransfer()}
            >
              {Utils.getTranslation(language, translator.general.suivant)}
            </Button>
          </>
        ) : (
          <>
            {!isTransferring && transferResults.length === 0 && (
              <>
                <Button onClick={handleBack}>
                  {Utils.getTranslation(language, translator.general.precedent)}
                </Button>
                <Button
                  onClick={handleConfirmTransfer}
                  variant="contained"
                  color="primary"
                >
                  {Utils.getTranslation(language, translator.general.transfer)}
                </Button>
              </>
            )}

            {(!isTransferring && transferResults.length > 0) && (
              <Button onClick={handleClose} variant="contained">
                {Utils.getTranslation(language, translator.general.close)}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransferClientsDialog;
